import React, { useState, useEffect } from "react";
import {
  User,
  GraduationCap,
  BookOpen,
  MapPin,
  Clock,
  Phone,
  Star,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  X,
} from "lucide-react";

const TutorSubmission = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    gender: "",
    profile_photo_url: "",
    short_bio: "",
    degree_id: "",
    institute_id: "",
    field_of_study_id: "",
    start_year: "",
    end_year: "",
    area_id: "",
    mode: "",
    contact_whatsapp: "",
    contact_phone: "",
    expected_remuneration: "",
    subjects: [],
    availability: [],
  });

  const [formOptions, setFormOptions] = useState({
    mediums: [],
    levels: [],
    subjects: [],
    divisions: [],
    districts: [],
    areas: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch form options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [mediums, levels, subjects, divisions, districts, areas] =
          await Promise.all([
            fetch("/api/mediums").then((res) => res.json()),
            fetch("/api/levels").then((res) => res.json()),
            fetch("/api/subjects").then((res) => res.json()),
            fetch("/api/divisions").then((res) => res.json()),
            fetch("/api/districts").then((res) => res.json()),
            fetch("/api/areas").then((res) => res.json()),
          ]);

        setFormOptions({
          mediums,
          levels,
          subjects,
          divisions,
          districts,
          areas,
        });
      } catch (error) {
        console.error("Error fetching options:", error);
      }
    };

    fetchOptions();
  }, []);

  const steps = [
    {
      title: "Personal Info",
      icon: User,
      description: "Tell us about yourself",
    },
    {
      title: "Education",
      icon: GraduationCap,
      description: "Your academic background",
    },
    {
      title: "Teaching Details",
      icon: BookOpen,
      description: "What you want to teach",
    },
    {
      title: "Location & Mode",
      icon: MapPin,
      description: "Where and how you teach",
    },
    {
      title: "Availability",
      icon: Clock,
      description: "When you're available",
    },
    {
      title: "Contact & Final",
      icon: Phone,
      description: "Contact details and finish",
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSubjectAdd = () => {
    setFormData((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        { medium_id: "", level_id: "", subject_id: "" },
      ],
    }));
  };

  const handleSubjectRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index),
    }));
  };

  const handleSubjectChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.map((subject, i) =>
        i === index ? { ...subject, [field]: value } : subject
      ),
    }));
  };

  const handleAvailabilityAdd = () => {
    setFormData((prev) => ({
      ...prev,
      availability: [
        ...prev.availability,
        { day: "", time_from: "", time_to: "" },
      ],
    }));
  };

  const handleAvailabilityRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      availability: prev.availability.filter((_, i) => i !== index),
    }));
  };

  const handleAvailabilityChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      availability: prev.availability.map((slot, i) =>
        i === index ? { ...slot, [field]: value } : slot
      ),
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.name) newErrors.name = "Name is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.short_bio) newErrors.short_bio = "Bio is required";
        break;
      case 1:
        if (!formData.degree_id) newErrors.degree_id = "Degree is required";
        if (!formData.institute_id)
          newErrors.institute_id = "Institute is required";
        if (!formData.field_of_study_id)
          newErrors.field_of_study_id = "Field of study is required";
        if (!formData.start_year)
          newErrors.start_year = "Start year is required";
        break;
      case 2:
        if (formData.subjects.length === 0)
          newErrors.subjects = "At least one subject is required";
        break;
      case 3:
        if (!formData.area_id) newErrors.area_id = "Area is required";
        if (!formData.mode) newErrors.mode = "Teaching mode is required";
        break;
      case 4:
        if (formData.availability.length === 0)
          newErrors.availability = "At least one availability slot is required";
        break;
      case 5:
        if (!formData.contact_phone)
          newErrors.contact_phone = "Phone number is required";
        if (!formData.contact_whatsapp)
          newErrors.contact_whatsapp = "WhatsApp number is required";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/tutors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowSuccess(true);
      } else {
        throw new Error("Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setErrors({ submit: "Failed to submit. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <User className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Personal Information
              </h2>
              <p className="text-gray-600">
                Let's start with the basics about you
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  value={formData.gender}
                  onChange={(e) => handleInputChange("gender", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.gender ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Picture URL
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="url"
                  value={formData.profile_photo_url}
                  onChange={(e) =>
                    handleInputChange("profile_photo_url", e.target.value)
                  }
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="https://example.com/photo.jpg"
                />
                <button className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors">
                  <Upload className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Short Bio *
              </label>
              <textarea
                value={formData.short_bio}
                onChange={(e) => handleInputChange("short_bio", e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                  errors.short_bio ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Tell us about yourself, your teaching philosophy, and what makes you unique..."
              />
              {errors.short_bio && (
                <p className="text-red-500 text-sm mt-1">{errors.short_bio}</p>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-teal-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <GraduationCap className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Educational Background
              </h2>
              <p className="text-gray-600">
                Share your academic qualifications
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Institute *
                </label>
                <input
                  type="text"
                  value={formData.institute_id}
                  onChange={(e) =>
                    handleInputChange("institute_id", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.institute_id ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="University/College name"
                />
                {errors.institute_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.institute_id}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Degree *
                </label>
                <input
                  type="text"
                  value={formData.degree_id}
                  onChange={(e) =>
                    handleInputChange("degree_id", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.degree_id ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Bachelor's, Master's, PhD, etc."
                />
                {errors.degree_id && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.degree_id}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Field of Study *
              </label>
              <input
                type="text"
                value={formData.field_of_study_id}
                onChange={(e) =>
                  handleInputChange("field_of_study_id", e.target.value)
                }
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.field_of_study_id
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Computer Science, Mathematics, Physics, etc."
              />
              {errors.field_of_study_id && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.field_of_study_id}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Year *
                </label>
                <input
                  type="number"
                  value={formData.start_year}
                  onChange={(e) =>
                    handleInputChange("start_year", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.start_year ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="2020"
                  min="1990"
                  max="2024"
                />
                {errors.start_year && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.start_year}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Year
                </label>
                <input
                  type="number"
                  value={formData.end_year}
                  onChange={(e) =>
                    handleInputChange("end_year", e.target.value)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="2024 (leave empty if ongoing)"
                  min="1990"
                  max="2030"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <BookOpen className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Teaching Subjects
              </h2>
              <p className="text-gray-600">
                What subjects do you want to teach?
              </p>
            </div>

            {formData.subjects.map((subject, index) => (
              <div
                key={index}
                className="p-6 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800">
                    Subject {index + 1}
                  </h3>
                  {formData.subjects.length > 1 && (
                    <button
                      onClick={() => handleSubjectRemove(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medium
                    </label>
                    <select
                      value={subject.medium_id}
                      onChange={(e) =>
                        handleSubjectChange(index, "medium_id", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select medium</option>
                      {formOptions.mediums.map((medium) => (
                        <option key={medium.id} value={medium.id}>
                          {medium.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Level
                    </label>
                    <select
                      value={subject.level_id}
                      onChange={(e) =>
                        handleSubjectChange(index, "level_id", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select level</option>
                      {formOptions.levels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <select
                      value={subject.subject_id}
                      onChange={(e) =>
                        handleSubjectChange(index, "subject_id", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select subject</option>
                      {formOptions.subjects.map((subj) => (
                        <option key={subj.id} value={subj.id}>
                          {subj.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={handleSubjectAdd}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
            >
              + Add Another Subject
            </button>

            {errors.subjects && (
              <p className="text-red-500 text-sm">{errors.subjects}</p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <MapPin className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Location & Teaching Mode
              </h2>
              <p className="text-gray-600">
                Where and how do you prefer to teach?
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area/Location *
              </label>
              <select
                value={formData.area_id}
                onChange={(e) => handleInputChange("area_id", e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.area_id ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select your area</option>
                {formOptions.areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
              {errors.area_id && (
                <p className="text-red-500 text-sm mt-1">{errors.area_id}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teaching Mode *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {["online", "offline", "both"].map((mode) => (
                  <div
                    key={mode}
                    onClick={() => handleInputChange("mode", mode)}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      formData.mode === mode
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-lg font-medium capitalize">
                        {mode}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {mode === "online" && "Video calls & virtual sessions"}
                        {mode === "offline" && "In-person tutoring"}
                        {mode === "both" && "Flexible online & offline"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.mode && (
                <p className="text-red-500 text-sm mt-1">{errors.mode}</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Clock className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Availability
              </h2>
              <p className="text-gray-600">When are you available to teach?</p>
            </div>

            {formData.availability.map((slot, index) => (
              <div
                key={index}
                className="p-6 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-800">
                    Time Slot {index + 1}
                  </h3>
                  {formData.availability.length > 1 && (
                    <button
                      onClick={() => handleAvailabilityRemove(index)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Day
                    </label>
                    <select
                      value={slot.day}
                      onChange={(e) =>
                        handleAvailabilityChange(index, "day", e.target.value)
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select day</option>
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From
                    </label>
                    <input
                      type="time"
                      value={slot.time_from}
                      onChange={(e) =>
                        handleAvailabilityChange(
                          index,
                          "time_from",
                          e.target.value
                        )
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To
                    </label>
                    <input
                      type="time"
                      value={slot.time_to}
                      onChange={(e) =>
                        handleAvailabilityChange(
                          index,
                          "time_to",
                          e.target.value
                        )
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={handleAvailabilityAdd}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-500 transition-colors"
            >
              + Add Another Time Slot
            </button>

            {errors.availability && (
              <p className="text-red-500 text-sm">{errors.availability}</p>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Phone className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Contact & Final Details
              </h2>
              <p className="text-gray-600">How can students reach you?</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) =>
                    handleInputChange("contact_phone", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.contact_phone ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="+880 1234 567890"
                />
                {errors.contact_phone && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.contact_phone}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Number *
                </label>
                <input
                  type="tel"
                  value={formData.contact_whatsapp}
                  onChange={(e) =>
                    handleInputChange("contact_whatsapp", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    errors.contact_whatsapp
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="+880 1234 567890"
                />
                {errors.contact_whatsapp && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.contact_whatsapp}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Remuneration (per hour)
              </label>
              <input
                type="number"
                value={formData.expected_remuneration}
                onChange={(e) =>
                  handleInputChange("expected_remuneration", e.target.value)
                }
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="500"
                min="0"
              />
              <p className="text-sm text-gray-600 mt-1">
                Amount in BDT (optional)
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-blue-900 mb-2">
                    Ready to Submit!
                  </h3>
                  <p className="text-blue-700 text-sm leading-relaxed">
                    Please review all your information before submitting. Once
                    submitted, your profile will be reviewed and you'll receive
                    a confirmation email with your unique tutor profile link.
                  </p>
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{errors.submit}</p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-green-500 rounded-full mx-auto mb-6 flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Success!</h2>
          <p className="text-gray-600 mb-6">
            Your tutor profile has been submitted successfully. You'll receive
            an email confirmation with your profile link shortly.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
          >
            Submit Another Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Become a Tutor
              </h1>
              <p className="text-gray-600 mt-1">
                Join our community of educators
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-5 h-5 text-yellow-500 fill-current" />
              <span className="text-sm text-gray-600">
                Step {currentStep + 1} of {steps.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between mb-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;

            return (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isCompleted
                        ? "bg-green-500 text-white"
                        : isActive
                        ? "bg-blue-500 text-white shadow-lg scale-110"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      <Icon className="w-6 h-6" />
                    )}
                  </div>
                  <div className="text-center mt-2">
                    <div
                      className={`text-sm font-medium ${
                        isActive
                          ? "text-blue-600"
                          : isCompleted
                          ? "text-green-600"
                          : "text-gray-500"
                      }`}
                    >
                      {step.title}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 hidden sm:block">
                      {step.description}
                    </div>
                  </div>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 rounded transition-all duration-300 ${
                      index < currentStep ? "bg-green-500" : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="min-h-[500px]">{renderStepContent()}</div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                currentStep === 0
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100 hover:shadow-md"
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>

            <div className="flex space-x-4">
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={nextStep}
                  className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <span>Continue</span>
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-teal-600 text-white px-8 py-3 rounded-lg font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Submit Profile</span>
                      <Star className="w-5 h-5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorSubmission;
